/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import MerchantList from "./components/MerchantList";
import MerchantSearch from "./components/MerchantSearch";
import MerchantToolbar from "./components/MerchantToolbar";
import { useNavigate } from "react-router-dom";

interface Filters {
  [key: string]: any;
}

const rowPerPage = 10;

const initialFilters: Filters = {
  name: "",
};

interface PayloadType {
  [key: string]: any;
}

const Merchant = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [lastKeys, setLastKeys] = useState<{ [key: number]: string }>({});
  const [currentLastKey, setCurrentLastKey] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(rowPerPage);
  const [rowCount, setRowCount] = useState(rowPerPage);
  const navigate = useNavigate();
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  const [showFilters, setShowFilters] = useState(false);

  // AbortController for cancelling requests on unmount
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  // Cleanup: Cancel all pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Create new AbortController for each batch of requests
  const getAbortSignal = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };

  useEffect(() => {
    const signal = getAbortSignal();
    const currentRequestId = ++requestIdRef.current;

    const fetchData = async () => {
      setIsLoading(true);
      await fetchMerchants({
        page: 0,
        limit: rowPerPage,
        lastkey: "",
        ...filters,
      }, signal, currentRequestId);
      if (signal.aborted || requestIdRef.current !== currentRequestId) return;
      setIsLoading(false);
    };

    fetchData();
  }, []);

  const fetchMerchants = async (payload: PayloadType = {}, signal?: AbortSignal, requestId?: number) => {
    try {
      const queryObject: PayloadType = {
        limit: payload?.limit,
        lastkey: payload?.lastkey,
        with_balance: true,
      };

      if (payload?.name !== "" && payload?.name !== undefined) {
        queryObject.name = payload?.name;
      }

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/merchant/all-merchants?${queryString}`,
        { signal }
      );

      if (signal?.aborted) return;
      if (requestId !== undefined && requestIdRef.current !== requestId) return;

      let newData = response.data.merchants?.payload;

      if (newData.length !== 0) {
        setTableData(newData);
      } else {
        setTableData([]);
      }

      let newLastKey = response.data.merchants?.lastkey;
      if (newLastKey) {
        setCurrentLastKey(newLastKey);
        let newPage = payload?.page;
        let newPageSize = payload?.limit;

        if (newData.length !== 0) {
          setRowCount((newPage + 1) * newPageSize + newPageSize);
        } else {
          setRowCount(newPage * newPageSize + newPageSize);
        }
        setLastKeys({ ...lastKeys, [newPage]: newLastKey });
      }
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      if (requestId !== undefined && requestIdRef.current !== requestId) return;
      setTableData([]);
      setPage(0);
      setPageSize(0);
      setRowCount(0);
      setLastKeys([]);
      setCurrentLastKey("");
      console.error("Error fetching data", error);
    }
  };

  const setPaginationModel = async (newValue: any) => {
    setIsLoading(true);

    let newFilters: any = {
      page: newValue.page,
      limit: newValue.pageSize,
      lastkey: currentLastKey,
      ...filters,
    };

    if (newValue.page < page) {
      newFilters = {
        ...newFilters,
        lastkey: newValue.page !== 0 ? lastKeys[newValue.page - 1] : "",
      };
    }

    if (newValue.pageSize !== pageSize) {
      newFilters = {
        page: 0,
        limit: newValue.pageSize,
        lastkey: "",
        ...filters,
      };
      setPage(0);
      setPageSize(newValue.pageSize);
      setCurrentLastKey("");
      setLastKeys([]);
    } else {
      setPage(newValue.page);
    }

    await fetchMerchants(newFilters);
    setIsLoading(false);
  };

  // const handleClickCreate = () => {
  //   navigate(`/${userRole}/merchant-management/create`);
  // };

  const handleSearchSubmit = async () => {
    setIsLoading(true);
    setCurrentLastKey("");
    setLastKeys({});
    setPage(0);
    await fetchMerchants({ page: 0, limit: rowPerPage, lastkey: "", ...filters });
    setIsLoading(false);
  };

  const handleSearchClear = () => {
    handleResetTable();
  };

  const handleResetTable = async () => {
    setIsLoading(true);
    setFilters(initialFilters);
    setLastKeys([]);
    setCurrentLastKey("");
    setPage(0);
    await fetchMerchants({
      page: 0,
      limit: rowPerPage,
      lastkey: "",
      ...initialFilters,
    });
    setIsLoading(false);
  };

  const handleClickCreate = () => {
    navigate(`/${userRole}/merchants/manage/create`);
  };

  const triggerReloadMerchantList = async () => {
    setIsLoading(true);
    await fetchMerchants({
      page: page,
      limit: rowPerPage,
      lastkey: page > 0 ? (lastKeys[page - 1] || "") : "",
      ...filters,
    });
    setIsLoading(false);
  };

  return (
    <>
      {/* {(userRole === 'admin' || userRole === 'subaccount') &&
        <div className="flex justify-start mt-6 mb-3">
          <Button
            onClick={() => handleClickCreate()}
            className="rounded-xl bg-primary-500 px-3 py-2.5 text-sm font-medium text-white transition duration-200 hover:bg-primary-600 active:bg-primary-700 dark:bg-primary-400 dark:text-white dark:hover:bg-primary-300 dark:active:bg-primary-200"
          >
            <FaPlus />&nbsp;Create Merchant
          </Button>
        </div>
      } */}

      <MerchantToolbar
        isLoading={isLoading}
        onToggleFilters={() => setShowFilters((p) => !p)}
        onRefresh={triggerReloadMerchantList}
        onNewMerchant={handleClickCreate}
      />
      <MerchantSearch
        showFilters={showFilters}
        isLoading={isLoading}
        filters={filters}
        setFilters={setFilters}
        handleSearchSubmit={handleSearchSubmit}
        handleSearchClear={handleSearchClear}
      />
      <MerchantList
        isLoading={isLoading}
        page={page}
        pageSize={pageSize}
        rowCount={rowCount}
        tableData={isLoading ? [] : tableData}
        setPaginationModel={setPaginationModel}
        currentLastKey={currentLastKey}
      />
    </>
  );
};

export default Merchant;
